import { api, I18N, React } from '@/app'
import { PaginationResponse, QueryManager } from '@/packages'
import { analytics } from '@/services/analytics/segment'
import { Module } from '@/types'
import { getListQueryParams, hurdleItem, useFlatlistProps } from '@/utils'
import { onUpdate, useI18N } from '@codeleap/common'
import { SegmentedControlOptionProps } from '@codeleap/web'
import { useQuery } from '@tanstack/react-query'
import { queryClient } from '../queryClient'
import { useAuthState } from '@/features/Auth/bridge/useAuthState'

const BASE_URL = 'learn/'

const blankModule: Module = {
  available: true,
  chapters: [],
  description: '',
  done: false,
  id: 0,
  image: '',
  is_free: false,
  is_new: false,
  is_placeholder: false,
  mobile_image: '',
  progress: 0,
  sources: [],
  title: '',
  web_image: '',
  created_datetime: String(new Date()),
  tags: [],
  type: null,
  updated_datetime: String(new Date())
}

function normalizeBetaGames(item: Module): Module {
  if (!item.beta_game) return item as Module
  if (item.flag === 'hurdle') return { ...item, ...blankModule, ...hurdleItem }
  return { ...blankModule, ...item, type: 'game' }
}

export const modulesManager = new QueryManager({
  itemType: {} as Module,
  name: 'modules',
  queryClient: queryClient.client,

  listItems: async (limit, offset, filters) => {
    const response = await api.get<PaginationResponse<Module>>(BASE_URL, {
      params: {
        limit,
        offset,
        ...filters,
        tags: getListQueryParams(filters?.tags),
        sources: getListQueryParams(filters?.sources),
        themes: getListQueryParams(filters?.themes)
      }
    })

    const results = response.data.results.map(normalizeBetaGames)

    return { ...response.data, results }
  },
  retrieveItem: async id => {
    const response = await api.get<Module>(`${BASE_URL}${id}/`)

    return response.data
  }
})

const queryKeys = {
  filterOptions: ['learn-filter-options']
}

export function useLearnFilterOptions() {
  const query = useQuery({
    queryKey: queryKeys.filterOptions,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const response = await api.get(`${BASE_URL}filters/`)
      return response.data
    }
  })
  return query.data
}

type ModuleFilters = {
  search: string
  sources: string[]
  tags: string[]
  themes: string[]
  type: string
}

type UseModulesParams = {
  sources?: string | string[]
}

export const useModules = ({ sources: _sources = null }: UseModulesParams) => {
  const { t } = useI18N()
  const { isLoggedIn } = useAuthState()
  const typeOptions = getTypeOptions()

  const sources = React.useMemo(() => {
    if (_sources === null) return []
    if (typeof _sources === 'string') {
      const parsed =
        _sources.length > 0
          ? _sources
              .split(',')
              .map(s => s.trim())
              .filter(Boolean)
          : []
      return parsed
    }
    if (Array.isArray(_sources)) {
      return _sources
    }
    return []
  }, [_sources])

  const [filter, setFilter] = React.useState<ModuleFilters>(() => ({
    search: '',
    sources,
    tags: [],
    themes: [],
    type: typeOptions[0].value
  }))

  React.useEffect(() => {
    if (JSON.stringify(filter.sources) !== JSON.stringify(sources)) {
      setFilter(f => ({ ...f, sources }))
    }
  }, [sources])

  const modules = modulesManager.use({ filter })

  const filterOptions = useLearnFilterOptions()

  const listProps = useFlatlistProps(modules, {
    noMoreItemsText: t('learn.noMoreCollections')
  })

  const changeType = React.useCallback((type: ModuleFilters['type'] | 'all') => {
    analytics.track(`lesson_${type}`)

    const normalizedType = type === 'all' ? '' : type
    setFilter(p => ({ ...p, type: normalizedType }))
  }, [])

  onUpdate(() => {
    if (isLoggedIn) {
      modules?.refresh()
    }
  }, [isLoggedIn])

  return {
    modulesListProps: {
      ...listProps
    },
    filter,
    setFilter,
    filterOptions,
    typeOptions,
    changeType
  }
}

function getTypeOptions(): SegmentedControlOptionProps[] {
  return [
    {
      label: I18N.t('learn.learnScreen.filterStatus.all'),
      value: ''
    },
    {
      label: I18N.t('learn.learnScreen.filterStatus.lessons'),
      value: 'lesson'
    },
    {
      label: I18N.t('learn.learnScreen.filterStatus.games'),
      value: 'game'
    },
    {
      label: I18N.t('learn.learnScreen.filterStatus.videos'),
      value: 'video'
    }
  ]
}

export type UseModulesReturn = ReturnType<typeof useModules>
